import { useEffect, useState } from "react";
import { Plus, Pencil, Languages } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { MediaUpload } from "../../components/admin/MediaUpload";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyPost = { slug: "", title_pt: "", excerpt_pt: "", content_pt: "", title_fr: "", excerpt_fr: "", content_fr: "", category: "Beleza", cover_image: "", published: false };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [editingId, setEditingId] = useState(null);
  const [translating, setTranslating] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const newPost = () => { setForm(emptyPost); setEditingId(null); setOpen(true); };
  const editPost = (p) => { setForm(p); setEditingId(p.id); setOpen(true); };

  const translate = async () => {
    setTranslating(true);
    try {
      const { data } = await api.post("/admin/blog/translate", { title_pt: form.title_pt, excerpt_pt: form.excerpt_pt, content_pt: form.content_pt });
      setForm({ ...form, ...data });
      toast.success("Traduzido para o francês!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTranslating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/blog/${editingId}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Artigo salvo!");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif text-[#9B6179]">Caderno de Beleza</h1>
        <Button onClick={newPost} data-testid="new-post-button" className="bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors"><Plus className="w-4 h-4 mr-1" /> Novo Artigo</Button>
      </div>

      <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase"><tr><th className="text-left px-4 py-3">Título (PT)</th><th className="text-left px-4 py-3">Categoria</th><th className="text-left px-4 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-[#9B6179]/5" data-testid={`blog-row-${p.slug}`}>
                <td className="px-4 py-3">{p.title_pt}</td><td className="px-4 py-3">{p.category}</td>
                <td className="px-4 py-3"><Badge className={p.published ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}>{p.published ? "Publicado" : "Rascunho"}</Badge></td>
                <td className="px-4 py-3 text-right"><button onClick={() => editPost(p)} data-testid={`edit-post-${p.slug}`}><Pencil className="w-4 h-4 text-[#6F3F55]" /></button></td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#6F3F55]">Nenhum artigo ainda.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="blog-modal">
          <form onSubmit={submit} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">{editingId ? "Editar" : "Novo"} Artigo</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Slug</Label><Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="blog-form-slug" /></div>
              <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="blog-form-category" /></div>
            </div>
            <div className="space-y-2"><Label>Imagem de capa</Label>
              {form.cover_image && <img src={form.cover_image} alt="" className="h-24 rounded-[2px] object-cover" data-testid="blog-form-cover-preview" />}
              <MediaUpload folder="blog" onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))} testid="blog-form-cover-upload" label="Enviar imagem de capa" />
              <Input placeholder="ou cole uma URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="blog-form-cover" /></div>

            <p className="text-xs uppercase tracking-widest text-[#9B6179] pt-2">Português (redija primeiro)</p>
            <div><Label>Título (PT)</Label><Input required value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} data-testid="blog-form-title-pt" /></div>
            <div><Label>Resumo (PT)</Label><Textarea rows={2} value={form.excerpt_pt} onChange={(e) => setForm({ ...form, excerpt_pt: e.target.value })} data-testid="blog-form-excerpt-pt" /></div>
            <div><Label>Conteúdo (PT)</Label><Textarea rows={6} value={form.content_pt} onChange={(e) => setForm({ ...form, content_pt: e.target.value })} data-testid="blog-form-content-pt" /></div>

            <Button type="button" variant="outline" disabled={translating || !form.content_pt} onClick={translate} className="w-full" data-testid="blog-translate-button">
              <Languages className="w-4 h-4 mr-2" /> {translating ? "Traduzindo..." : "Traduzir para o Francês"}
            </Button>

            <p className="text-xs uppercase tracking-widest text-[#9B6179] pt-2">Français</p>
            <div><Label>Titre (FR)</Label><Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} data-testid="blog-form-title-fr" /></div>
            <div><Label>Résumé (FR)</Label><Textarea rows={2} value={form.excerpt_fr} onChange={(e) => setForm({ ...form, excerpt_fr: e.target.value })} data-testid="blog-form-excerpt-fr" /></div>
            <div><Label>Contenu (FR)</Label><Textarea rows={6} value={form.content_fr} onChange={(e) => setForm({ ...form, content_fr: e.target.value })} data-testid="blog-form-content-fr" /></div>

            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="blog-form-published" /><Label>Publicado no site</Label></div>
            <Button type="submit" className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="blog-form-submit">Salvar Artigo</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
